import {
  convertLabelTypeToExperimentType,
  convertLabelTypeToExperimentTypeForCredits,
} from '@clef/shared/utils';
import * as t from '@clef/shared/types';
import { Tensor } from 'onnxruntime-web';
import { parseNpy } from '@clef/client-library/src/utils/npy';
import {
  ActionItemsResponse,
  ApiResponse,
  BoxSuggestion,
  ConfusionMatrixKey,
  CorrelatedTrainingImagesResponse,
  LabelType,
  MediaId,
  ProjectId,
  TrainHealthData,
  ModelArchSchemaItem,
  GetTrainingCreditCostRequest,
} from '@clef/shared/types';

import { API_GATEWAY_URL } from '../constants';
import { BaseAPI } from './base_api';

export const prefix = 'experiment_report';

type JobId = t.JobInfo['jobId'];

export type SAM2ImageEmbeddingResult = {
  imageEmbedding: Tensor;
  highResFeats0: Tensor;
  highResFeats1: Tensor;
};

// SAM2 embedding tensor shapes and sizes
const SAM2_IMAGE_EMBED_SHAPE = [1, 256, 64, 64] as const;
const SAM2_HIGH_RES_FEATS_0_SHAPE = [1, 32, 256, 256] as const;
const SAM2_HIGH_RES_FEATS_1_SHAPE = [1, 64, 128, 128] as const;
const SAM2_IMAGE_EMBED_SIZE = 1 * 256 * 64 * 64; // 1,048,576
const SAM2_HIGH_RES_FEATS_0_SIZE = 1 * 32 * 256 * 256; // 2,097,152
const SAM2_HIGH_RES_FEATS_1_SIZE = 1 * 64 * 128 * 128; // 1,048,576

// Parses a flattened SAM2 embedding from a single .npy file.
// The backend concatenates 3 tensors: image_embed, high_res_feats_0, high_res_feats_1
// Source: landing-ai/experiment-report, landing-ai/avi-common
function parseEmbeddingFromNpy(npy: ArrayBuffer): SAM2ImageEmbeddingResult {
  const { data } = parseNpy(npy);
  const float32Data = data as Float32Array;

  const expectedTotal =
    SAM2_IMAGE_EMBED_SIZE + SAM2_HIGH_RES_FEATS_0_SIZE + SAM2_HIGH_RES_FEATS_1_SIZE;
  if (float32Data.length !== expectedTotal) {
    throw new Error(
      `Unexpected SAM2 embedding size: expected ${expectedTotal}, got ${float32Data.length}`,
    );
  }

  let offset = 0;
  const imageEmbedData = float32Data.slice(offset, offset + SAM2_IMAGE_EMBED_SIZE);
  offset += SAM2_IMAGE_EMBED_SIZE;

  const highResFeats0Data = float32Data.slice(offset, offset + SAM2_HIGH_RES_FEATS_0_SIZE);
  offset += SAM2_HIGH_RES_FEATS_0_SIZE;

  const highResFeats1Data = float32Data.slice(offset, offset + SAM2_HIGH_RES_FEATS_1_SIZE);

  return {
    imageEmbedding: new Tensor('float32', imageEmbedData, [...SAM2_IMAGE_EMBED_SHAPE]),
    highResFeats0: new Tensor('float32', highResFeats0Data, [...SAM2_HIGH_RES_FEATS_0_SHAPE]),
    highResFeats1: new Tensor('float32', highResFeats1Data, [...SAM2_HIGH_RES_FEATS_1_SHAPE]),
  };
}

class ExperimentReportApi extends BaseAPI {
  async getSingleModelReport(params: {
    projectId: t.ProjectId;
    jobId: t.JobInfo['id'];
  }): Promise<t.SingleModelReport> {
    const { jobId, ...queryParams } = params;
    return this.get(`v1/reports/${jobId}`, queryParams, true);
  }

  async getSingleModelReportDetails(params: {
    projectId: t.ProjectId;
    jobId: t.JobInfo['id'];
    limit: number;
    offset: number;
    groundTruth?: string;
    prediction?: string;
    confusionType?: t.ConfusionMatrixKey;
  }): Promise<t.SingleModelReportDetails> {
    const { jobId, ...queryParams } = params;
    return this.get(`v1/reports/${jobId}/details`, queryParams, true);
  }

  async getComparisonReport(params: {
    candidateJobId: t.JobInfo['id'];
    baselineJobId: t.JobInfo['id'];
    projectId: t.ProjectId;
  }): Promise<t.ComparisonReport> {
    const { baselineJobId, candidateJobId, ...queryParams } = params;
    return this.get(
      `v1/reports/${baselineJobId}`,
      { compareWith: candidateJobId, ...queryParams },
      true,
    );
  }

  async getComparisonReportDetails(params: {
    candidateJobId: t.JobInfo['id'];
    baselineJobId: t.JobInfo['id'];
    projectId: t.ProjectId;
    limit: number;
    offset: number;
    groundTruth?: string;
    prediction?: string;
    candidatePrediction?: string;
    confusionType?: t.ConfusionMatrixKey;
  }): Promise<t.ComparisonReportDetails> {
    const { baselineJobId, candidateJobId, ...queryParams } = params;
    return this.get(
      `v1/reports/${baselineJobId}/details`,
      { compareWith: candidateJobId, ...queryParams },
      true,
    );
  }

  async getAEAStatusByConfusionkey(params: {
    jobId: t.JobInfo['id'];
    confusionType: ConfusionMatrixKey;
  }): Promise<{ [key: string]: string[] }> {
    const { jobId, confusionType } = params;

    return this.get(`v1/reports/${jobId}/aea/status?errorType=${confusionType}`, undefined, true);
  }

  async getAllActionItems(params: { jobId: JobId }): Promise<ActionItemsResponse[]> {
    const { jobId } = params;

    return this.get(`v1/reports/${jobId}/aea/actions_detail`, undefined, true);
  }

  async addActionableErrorAnalysisActionItems(params: {
    jobId: JobId;
    mediaId: string;
    errorId: string;
    errorType: ConfusionMatrixKey;
    defectId: number;
    defectName: string;
    actionItems: string[];
  }) {
    const { jobId, mediaId, errorId, errorType, defectId, defectName, actionItems } = params;
    const body = {
      mediaId,
      actionItems,
      errorId,
      errorType,
      defectId,
      defectName,
    };

    return this.post(`v1/reports/${jobId}/aea/actions_detail`, JSON.stringify(body));
  }

  async getAEALatestActionItemsByErrorId(params: {
    jobId: string;
    mediaId: number;
    errorId: string;
  }) {
    const { jobId, mediaId, errorId } = params;

    return this.get(
      `v1/reports/${jobId}/aea/actions_detail?mediaId=${mediaId}&errorId=${errorId}`,
      undefined,
      true,
    );
  }

  async getCorrelatedTrainingImages(params: {
    jobId: JobId;
    mediaId: MediaId;
    errorId: string;
    projectId: ProjectId;
  }): Promise<CorrelatedTrainingImagesResponse[]> {
    const { jobId, mediaId, errorId, projectId } = params;

    return this.get(
      `v1/reports/${jobId}/aea/similar_predictions?mediaId=${mediaId}&errorId=${errorId}&projectId=${projectId}`,
      undefined,
      true,
    );
  }

  async getSuggestions(projectId: number): Promise<{
    suggestions: {
      media_id: number;
      suggestions: BoxSuggestion[];
    }[];
    total: number;
  }> {
    // TODO: pagination
    return this.get(`v1/labels/suggestions/latest`, { projectId, limit: 100 }, true);
  }

  async updateSuggestionStatus(
    projectId: number,
    suggestionId: number,
    status: 'accepted' | 'rejected',
  ) {
    return this.patch(`v1/labels/suggestions/${suggestionId}?projectId=${projectId}`, { status });
  }

  async createSuggestionTask(projectId: number) {
    return this.post(`v1/labels/suggestion-tasks?projectId=${projectId}`);
  }

  async getSuggestionTaskStatus(projectId: number): Promise<{
    id: string;
    source: 'user-request' | 'train-job' | 'label-auto-trigger';
    status: 'starting' | 'running' | 'succeeded' | 'failed';
  }> {
    return this.get('v1/labels/suggestion-tasks/latest', { projectId }, true);
  }

  async savePostProcessing(projectId: ProjectId, postProcessing: Object) {
    return this.putJSON(
      `v1/postprocessing/segmentation-config?projectId=${projectId}`,
      postProcessing,
    );
  }

  async loadPostProcessing(projectId: ProjectId): Promise<
    ApiResponse<{
      classificationRules: {
        defaultClassification: string;
        rules: any[];
      };
      defectMap: Record<
        number,
        {
          defectId?: number;
          name: string;
        }
      >;
      noiseFilter: Record<number, number>;
    }>
  > {
    return this.get(`v1/postprocessing/segmentation-config?projectId=${projectId}`);
  }

  async startInstantLearningTrain(
    projectId: ProjectId,
    mediaId: number,
  ): Promise<
    ApiResponse<{
      jobId: string;
    }>
  > {
    return this.post(
      `v1/train/instant-learning-jobs?projectId=${projectId}&currentViewMediaId=${mediaId}`,
      null,
    );
  }

  async getTrainHealth(): Promise<TrainHealthData> {
    return this.get('v1/train/health', undefined, true);
  }

  async getSAMOnnxModel(): Promise<ArrayBuffer> {
    const controller = new AbortController();
    const { signal } = controller;

    // set timer
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        controller.abort();
        reject(
          new Error(
            'Smart Labeling is currently unavailable due to high demand. Please try again later.',
          ),
        );
      }, 30_000);
    });

    const requestPromise = fetch(
      `https://dr23pab8nlq87.cloudfront.net/sam/assist-label-onnx-model.onnx`,
      {
        method: 'GET',
        signal,
      },
    ).then(res => res.arrayBuffer());

    return Promise.race([timeoutPromise, requestPromise]);
  }

  async getSAM2ImageEmbedding(
    projectId: ProjectId,
    mediaPath: string,
  ): Promise<SAM2ImageEmbeddingResult> {
    const controller = new AbortController();
    const { signal } = controller;

    // set timer
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        controller.abort();
        reject(
          new Error(
            'Smart Labeling is currently unavailable due to high demand. Please try again later.',
          ),
        );
      }, 30000);
    });

    // Response format: Binary numpy array (np.save format) containing flattened SAM2 embeddings.
    // The array is a concatenation of 3 feature tensors:
    // - image_embed: [1, 256, 64, 64] -> 1,048,576 floats
    // - high_res_feats_0: [1, 32, 256, 256] -> 2,097,152 floats
    // - high_res_feats_1: [1, 64, 128, 128] -> 1,048,576 floats
    // Total: ~4.2M float values in a 1D array
    // Source: landing-ai/experiment-report (service/routers/embeddings.py)
    const requestPromise = this.get(
      `v1/embeddings/sam2-embeddings`,
      {
        projectId,
        mediaPath,
      },
      false,
      { signal },
      true,
    ).then((npyData: ArrayBuffer) => parseEmbeddingFromNpy(npyData));

    return Promise.race([timeoutPromise, requestPromise]);
  }

  async getCustomTrainingCreditCost(
    params: Omit<GetTrainingCreditCostRequest, 'experimentType'> & { labelType: LabelType },
  ): Promise<ApiResponse<{ trainCredits: number }>> {
    const { labelType, ...otherParams } = params;
    return this.get('v1/credits/train', {
      ...otherParams,
      experimentType: convertLabelTypeToExperimentTypeForCredits(params.labelType),
    });
  }

  async getCustomTrainingInferenceCost(params: {
    modelSize: string;
    height: number;
    width: number;
    labelType: LabelType;
  }): Promise<ApiResponse<{ inferenceCredits: number }>> {
    const { labelType, ...otherParams } = params;
    return this.get('v1/credits/inference', {
      ...otherParams,
      experimentType: convertLabelTypeToExperimentTypeForCredits(params.labelType),
    });
  }

  async getModelInferenceCost(params: {
    jobId: string;
    projectId: ProjectId;
  }): Promise<ApiResponse<{ inferenceCredits: number }>> {
    return this.get(`v1/credits/inference/${params.jobId}`, {
      projectId: params.projectId,
    });
  }

  async getModelArchSchemas(
    projectId: number,
    labelType: LabelType,
  ): Promise<ModelArchSchemaItem[]> {
    return this.get(
      `train/model-schemas`,
      { projectId, experimentType: convertLabelTypeToExperimentType(labelType) },
      true,
    );
  }
}

export default new ExperimentReportApi(prefix, API_GATEWAY_URL);
